import { create } from 'zustand';
import type { DocumentMeta } from '~/hooks/queries/use-documents';

/**
 * FRONT-634: mirrors `coding-open-store` — a transient signal to open a
 * specific chat's zen-modal conversation, raised from elsewhere in the app
 * (the Inspector's monitored-chats list). `title` rides along so the modal
 * can show the right header immediately instead of flashing "New chat" while
 * the thread list catches up. The chat launcher (which owns the modal) watches
 * `openRequest`, opens it, then clears the request.
 *
 * FRONT-1550: `returnTo` rides along too — the page path (+ query/hash) the
 * request was raised from, or `null` when it was raised from Home itself.
 * The host that consumes the request stashes it beside the opened asset and
 * navigates back to it when the modal closes, so closing a conversation
 * window opened from elsewhere returns the user to that page instead of
 * leaving them on Home.
 */
interface ChatOpenRequest {
  sessionId: string;
  title: string | null;
  returnTo: string | null;
  /** FRONT-2522: set when the request opens a chat pinned to one agent (the
   *  Agents page's "Chat directly" button). The launcher puts it on the asset
   *  it builds. Absent for every other opener. */
  directAgent?: { id: string; name: string };
}

interface ChatOpenState {
  openRequest: ChatOpenRequest | null;
  requestOpenChat: (sessionId: string, title?: string | null, returnTo?: string | null) => void;
  /** FRONT-2522: open a NEW chat pinned to one agent. Mints the session id
   *  here (same `web:<uuid>` shape `newChatAsset` uses) so the launcher can
   *  treat it as an ordinary open request. */
  requestOpenDirectAgentChat: (agent: { id: string; name: string }, returnTo?: string | null) => void;
  clearOpenChat: () => void;
  /**
   * FRONT-874: the same one-shot cross-page signal, for opening a memory
   * DOCUMENT in the zen chat modal (not a chat thread) — raised from the
   * command palette on a non-home page. Carries the full `DocumentMeta` hit
   * so `home.tsx` can hand it straight to `documentToFeedItem` without
   * re-deriving the mapping. `home.tsx` (which owns the Files-style modal
   * instance) watches `openDocumentRequest`, opens it, then clears it.
   *
   * FRONT-1550: carries `returnTo` alongside the document, same contract as
   * `openRequest` above.
   */
  openDocumentRequest: { doc: DocumentMeta; returnTo: string | null } | null;
  requestOpenDocument: (doc: DocumentMeta, returnTo?: string | null) => void;
  clearOpenDocument: () => void;
}

export const useChatOpenStore = create<ChatOpenState>((set) => ({
  openRequest: null,
  requestOpenChat: (sessionId, title = null, returnTo = null) =>
    set({ openRequest: { sessionId, title, returnTo } }),
  requestOpenDirectAgentChat: (agent, returnTo = null) =>
    set({
      openRequest: {
        sessionId: `web:${crypto.randomUUID()}`,
        title: null,
        returnTo,
        directAgent: { id: agent.id, name: agent.name },
      },
    }),
  clearOpenChat: () => set({ openRequest: null }),
  openDocumentRequest: null,
  requestOpenDocument: (doc, returnTo = null) => set({ openDocumentRequest: { doc, returnTo } }),
  clearOpenDocument: () => set({ openDocumentRequest: null }),
}));
